// src/pages/admin/UserManagementPage.jsx
//
// User Management – Admin page (LeaveTypesPage-like UI)
// =====================================================
//
// Key layout fix (your issue):
// - The page is now a "full-height flex layout":
//   header + alerts + card (flex:1).
// - The card contains:
//   topbar (filters) + content area (flex:1).
// - The ONLY scrolling area on desktop is the table wrapper.
// - The footnote is OUTSIDE the scroll area, so it is always visible.
//
// This avoids "bottom being cut off" on smaller monitors,
// even if your app shell disables body scrolling.
//
// IMPORTANT ARCHITECTURAL FIX
// ---------------------------
// Password reset on this page is an ADMIN user-management operation.
// Therefore it should be sent through userService, not through AuthContext.
//
// Why this matters:
// - AuthContext is about the currently authenticated session/user.
// - UserManagementPage edits OTHER users as an admin.
// - Therefore resetUserPassword must come from ../../services/userService.
//
// This is the key fix for the runtime error:
//
//   TypeError: resetUserPassword is not a function
//
// because the old version tried to read resetUserPassword from AuthContext,
// where it does not exist (or is not exposed in this app version).

import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import {
  fetchUsersAdminAll,
  updateUserField,
  deleteUserById,
  resetUserPassword,
  getApiErrorMessage,
} from "../../services/userService";

import useMediaQuery from "../../hooks/useMediaQuery";
import UserCard from "../../components/UserCard";

import "./UserManagementPage.css";

/* -------------------------------------------------------------------------- */
/* Helpers                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * Convert different backend-ish boolean shapes into strict boolean.
 *
 * Supported examples:
 * - true / false
 * - 1 / 0
 * - "true" / "false"
 * - "yes" / "no"
 *
 * We keep this helper because admin tables often receive mixed shapes
 * from legacy rows / serializers / partial migrations.
 */
function asBool(v) {
  if (v === true || v === false) return v;
  if (v == null) return false;

  const s = String(v).trim().toLowerCase();
  return s === "1" || s === "true" || s === "yes";
}

/**
 * Convert nullable/unknown value to safe string.
 */
function safeStr(v) {
  return v == null ? "" : String(v);
}

/**
 * Normalize search text so filtering stays predictable.
 */
function normalizeSearch(s) {
  return safeStr(s).trim().toLowerCase();
}

/**
 * Human-friendly label for user role.
 *
 * We keep this separate because:
 * - backend stores compact machine values,
 * - UI should stay readable,
 * - new roles can be added later in one place.
 */
function roleLabel(role, t) {
  const r = safeStr(role).toLowerCase();

  /**
   * i18n note:
   * Backend role values must stay unchanged because they are authorization /
   * persistence codes. Only the human-readable label is translated here.
   */
  if (r === "superuser") return t("admin:roles.superuser");
  if (r === "admin") return t("admin:roles.admin");
  if (r === "reception") return t("admin:roles.reception");
  if (r === "user") return t("admin:roles.user");

  return r ? r : "—";
}

/* -------------------------------------------------------------------------- */
/* Component                                                                  */
/* -------------------------------------------------------------------------- */

export default function UserManagementPage() {
  const navigate = useNavigate();
  const { t } = useTranslation(["admin", "common"]);

  // Desktop vs cards view
  const isMobile = useMediaQuery("(max-width: 992px)");

  // Data
  const [users, setUsers] = useState([]);

  // UI state
  const [loading, setLoading] = useState(false);

  /**
   * busyUserIds
   * -----------
   * Tracks row-level async operations.
   *
   * Why row-level busy tracking is useful:
   * - one user row can be saving while the rest of the table stays usable
   * - avoids blocking the whole page for one password reset / toggle
   * - gives nicer UX than one giant "page is loading" switch
   */
  const [busyUserIds, setBusyUserIds] = useState(() => new Set());

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Filters / search
  const [q, setQ] = useState("");
  const [roleFilter, setRoleFilter] = useState("ALL");
  const [activeFilter, setActiveFilter] = useState("ALL");

  /**
   * Drafts
   * ------
   * Controlled form drafts for inline editing.
   *
   * Why we keep drafts separate from `users`:
   * - controlled inputs stay smooth
   * - blur-based save is easier
   * - rollback on failed save is easier
   */
  const [drafts, setDrafts] = useState({});

  /**
   * Password drafts per user.
   *
   * We intentionally keep these local-only and never copy them into `users`.
   */
  const [passwords, setPasswords] = useState({});

  /* ----------------------------- Load users ------------------------------ */

  /**
   * Load the full admin-visible user list.
   *
   * Also hydrates draft state for inline editing.
   */
  const loadUsers = useCallback(async () => {
    setLoading(true);
    setError("");
    setSuccess("");

    try {
      const list = await fetchUsersAdminAll();
      const safeList = Array.isArray(list) ? list : [];

      setUsers(safeList);

      /**
       * Draft hydration strategy:
       * - keep already typed drafts if they exist
       * - initialize missing rows from freshly loaded server data
       *
       * This avoids clobbering in-progress edits unnecessarily.
       */
      setDrafts((prev) => {
        const next = { ...(prev || {}) };

        for (const u of safeList) {
          const id = Number(u.id);

          next[id] = next[id] || {
            first_name: safeStr(u.first_name),
            last_name: safeStr(u.last_name),
            role: safeStr(u.role) || "user",
            is_approved: asBool(u.is_approved),
            active: asBool(u.active),
          };
        }

        return next;
      });
    } catch (e) {
      console.error("[UserManagementPage] ❌ loadUsers failed:", e);
      setError(getApiErrorMessage(e, t("admin:userManagement.messages.loadFailed")));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  /**
   * Auto-clear alerts after a few seconds.
   *
   * This keeps the page calm and avoids stale messages hanging around
   * after the user has already continued working.
   */
  useEffect(() => {
    if (!success && !error) return;

    const t = setTimeout(() => {
      setSuccess("");
      setError("");
    }, 3000);

    return () => clearTimeout(t);
  }, [success, error]);

  /* --------------------------- Derived filters --------------------------- */

  /**
   * Filtered list used by both:
   * - desktop table
   * - mobile card layout
   *
   * Filtering is based on:
   * - text query
   * - role filter
   * - active filter
   *
   * IMPORTANT:
   * We prefer draft values (if present) so the UI feels coherent
   * while the user edits inline fields.
   */
  const filteredUsers = useMemo(() => {
    const query = normalizeSearch(q);

    return users.filter((u) => {
      const id = Number(u.id);
      const d = drafts[id] || {};

      const hay = [
        safeStr(u.username),
        safeStr(d.first_name || u.first_name),
        safeStr(d.last_name || u.last_name),
      ]
        .join(" ")
        .toLowerCase();

      if (query && !hay.includes(query)) return false;

      if (roleFilter !== "ALL") {
        const r = safeStr(d.role || u.role).toLowerCase();
        if (r !== roleFilter.toLowerCase()) return false;
      }

      if (activeFilter !== "ALL") {
        const a = asBool(d.active ?? u.active);
        if (activeFilter === "ACTIVE" && !a) return false;
        if (activeFilter === "INACTIVE" && a) return false;
      }

      return true;
    });
  }, [users, drafts, q, roleFilter, activeFilter]);

  /**
   * Topbar statistics.
   *
   * These use server rows, not drafts, because they represent saved state,
   * not in-progress local edits.
   */
  const stats = useMemo(() => {
    const total = users.length;
    const active = users.filter((u) => asBool(u.active)).length;
    const approved = users.filter((u) => asBool(u.is_approved)).length;

    return { total, active, approved };
  }, [users]);

  /* ------------------------------ Busy set ------------------------------ */

  const markBusy = (userId, on) => {
    setBusyUserIds((prev) => {
      const next = new Set(prev);

      if (on) next.add(String(userId));
      else next.delete(String(userId));

      return next;
    });
  };

  const isBusy = (userId) => busyUserIds.has(String(userId));

  /* ------------------------------ Drafting ------------------------------ */

  /**
   * Patch one user's draft object.
   *
   * This is intentionally tiny and generic so all fields can reuse it.
   */
  const patchDraft = (userId, patch) => {
    const id = Number(userId);

    setDrafts((prev) => {
      const cur = prev?.[id] || {};
      return { ...(prev || {}), [id]: { ...cur, ...(patch || {}) } };
    });
  };

  /* ------------------------ Save single field (API) ---------------------- */

  /**
   * Save one field immediately to backend.
   *
   * Current use cases:
   * - first_name / last_name on blur
   * - role on change
   * - is_approved / active on change
   *
   * UX strategy:
   * - optimistic update for the visible row
   * - rollback on failure
   * - row-level busy state
   */
  const handleFieldUpdate = async (userId, field, value) => {
    const id = Number(userId);

    setError("");
    setSuccess("");
    markBusy(id, true);

    // Save previous state for rollback if request fails.
    const prevUsers = users;

    setUsers((prev) =>
      prev.map((u) => (Number(u.id) === id ? { ...u, [field]: value } : u))
    );

    try {
      await updateUserField(id, field, value);
      setSuccess(t("admin:userManagement.messages.fieldUpdated", { field: t(`admin:userManagement.fields.${field}`, field.replaceAll("_", " ")) }));
    } catch (e) {
      console.error(`[UserManagementPage] ❌ update ${field} failed:`, e);
      setError(getApiErrorMessage(e, t("admin:userManagement.messages.fieldUpdateFailed", { field: t(`admin:userManagement.fields.${field}`, field.replaceAll("_", " ")) })));

      // Roll back optimistic row update.
      setUsers(prevUsers);

      // Also roll back draft values so visible inputs match persisted state again.
      const rollbackUser = prevUsers.find((u) => Number(u.id) === id);
      if (rollbackUser) {
        patchDraft(id, {
          first_name: safeStr(rollbackUser.first_name),
          last_name: safeStr(rollbackUser.last_name),
          role: safeStr(rollbackUser.role) || "user",
          is_approved: asBool(rollbackUser.is_approved),
          active: asBool(rollbackUser.active),
        });
      }
    } finally {
      markBusy(id, false);
    }
  };

  /* --------------------------- Reset password ---------------------------- */

  /**
   * Reset password for one selected user.
   *
   * IMPORTANT:
   * This now uses resetUserPassword imported from userService,
   * which is the correct source for admin user-management operations.
   *
   * That is the functional fix for the original runtime error.
   */
  const handleResetPassword = async (userId) => {
    const id = Number(userId);
    const newPassword = passwords[id];

    setError("");
    setSuccess("");

    if (typeof newPassword !== "string" || newPassword.length === 0) {
      setError(t("admin:userManagement.messages.newPasswordRequired"));
      return;
    }

    markBusy(id, true);

    try {
      await resetUserPassword(id, newPassword);

      setSuccess(t("admin:userManagement.messages.passwordReset", { id }));

      // Clear only the affected password input after successful reset.
      setPasswords((prev) => ({ ...(prev || {}), [id]: "" }));
    } catch (e) {
      console.error("[UserManagementPage] ❌ reset password failed:", e);
      setError(getApiErrorMessage(e, t("admin:userManagement.messages.passwordResetFailed")));
    } finally {
      markBusy(id, false);
    }
  };

  /* ------------------------------- Delete -------------------------------- */

  /**
   * Delete selected user after confirmation.
   */
  const handleDeleteUser = async (userId) => {
    const id = Number(userId);

    setError("");
    setSuccess("");

    if (
      !window.confirm(
        t("admin:userManagement.messages.deleteConfirm")
      )
    ) {
      return;
    }

    markBusy(id, true);

    try {
      await deleteUserById(id);

      setUsers((prev) => prev.filter((u) => Number(u.id) !== id));
      setSuccess(t("admin:userManagement.messages.userDeleted", { id }));
    } catch (e) {
      console.error("[UserManagementPage] ❌ delete failed:", e);
      setError(getApiErrorMessage(e, t("admin:userManagement.messages.deleteFailed")));
    } finally {
      markBusy(id, false);
    }
  };

  /* ----------------------------- UI actions ------------------------------ */

  const openSettings = (userId) => navigate(`/user-settings/${userId}`);

  /* ---------------------------------------------------------------------- */
  /* Render                                                                 */
  /* ---------------------------------------------------------------------- */

  return (
    <div className="um-page">
      <div className="um-page__inner">
        {/* Header */}
        <div className="um-page__header">
          <div>
            <h1>{t("admin:userManagement.title")}</h1>
            <p>{t("admin:userManagement.subtitle")}</p>
          </div>

          <div className="um-page__headerActions">
            <button className="um-btn um-btn--ghost" onClick={() => navigate(-1)}>
              {t("admin:common.backWithArrow")}
            </button>
            <button className="um-btn um-btn--ghost" onClick={loadUsers} disabled={loading}>
              {t("admin:userManagement.actions.refreshWithIcon")}
            </button>
          </div>
        </div>

        {/* Alerts */}
        {error && <div className="um-alert um-alert--error">{error}</div>}
        {success && <div className="um-alert um-alert--success">{success}</div>}

        {/* Card */}
        <div className="um-card">
          {/* Topbar (meta + filters) */}
          <div className="um-topbar">
            <div className="um-meta">
              <span>
                {t("admin:userManagement.stats.total")}: <b>{stats.total}</b>
              </span>
              <span>
                {t("admin:userManagement.stats.active")}: <b>{stats.active}</b>
              </span>
              <span>
                {t("admin:userManagement.stats.approved")}: <b>{stats.approved}</b>
              </span>
            </div>

            <div className="um-filters">
              <div className="um-filter">
                <label>{t("admin:userManagement.filters.search")}</label>
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder={t("admin:userManagement.filters.searchPlaceholder")}
                />
              </div>

              <div className="um-filter">
                <label>{t("admin:userManagement.filters.role")}</label>
                <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}>
                  <option value="ALL">{t("admin:userManagement.filters.all")}</option>
                  <option value="user">{t("admin:roles.user")}</option>
                  <option value="superuser">{t("admin:roles.superuser")}</option>
                  <option value="admin">{t("admin:roles.admin")}</option>
                  <option value="reception">{t("admin:roles.reception")}</option>
                </select>
              </div>

              <div className="um-filter">
                <label>{t("admin:userManagement.filters.active")}</label>
                <select value={activeFilter} onChange={(e) => setActiveFilter(e.target.value)}>
                  <option value="ALL">{t("admin:userManagement.filters.all")}</option>
                  <option value="ACTIVE">{t("admin:userManagement.filters.activeOnly")}</option>
                  <option value="INACTIVE">{t("admin:userManagement.filters.inactiveOnly")}</option>
                </select>
              </div>
            </div>
          </div>

          {/* Content area (flex:1) */}
          {loading ? (
            <div className="um-loading">{t("admin:userManagement.states.loading")}</div>
          ) : filteredUsers.length === 0 ? (
            <div className="um-empty">{t("admin:userManagement.states.empty")}</div>
          ) : isMobile ? (
            // Mobile/tablet cards: the cards container is the scrollable area
            <div className="um-tableArea">
              <div className="um-cardsWrap">
                <div className="um-cardsGrid">
                  {filteredUsers.map((user) => {
                    const id = Number(user.id);

                    return (
                      <UserCard
                        key={id}
                        user={user}
                        passwordValue={passwords[id]}
                        onPasswordChange={(uid, val) =>
                          setPasswords((prev) => ({ ...(prev || {}), [uid]: val }))
                        }
                        onFieldUpdate={handleFieldUpdate}
                        onResetPassword={handleResetPassword}
                        onOpenSettings={() => openSettings(id)}
                        onDelete={() => handleDeleteUser(id)}
                        isLoading={isBusy(id)}
                        onToggleActive={(checked) => handleFieldUpdate(id, "active", checked)}
                      />
                    );
                  })}
                </div>
              </div>

              <div className="um-footnote">
                {t("admin:userManagement.footnote")}
              </div>
            </div>
          ) : (
            // Desktop table: ONLY the table wrapper scrolls; footnote stays visible
            <div className="um-tableArea">
              <div className="um-tableWrap">
                <table className="um-table">
                  <thead>
                    <tr>
                      <th className="um-left">{t("admin:userManagement.table.username")}</th>
                      <th className="um-left">{t("admin:userManagement.table.firstName")}</th>
                      <th className="um-left">{t("admin:userManagement.table.lastName")}</th>
                      <th className="um-left">{t("admin:userManagement.table.role")}</th>
                      <th>{t("admin:userManagement.table.approved")}</th>
                      <th>{t("admin:userManagement.table.active")}</th>
                      <th className="um-left">{t("admin:userManagement.table.newPassword")}</th>
                      <th className="um-right">{t("admin:userManagement.table.actions")}</th>
                    </tr>
                  </thead>

                  <tbody>
                    {filteredUsers.map((u) => {
                      const id = Number(u.id);

                      const d =
                        drafts[id] || {
                          first_name: safeStr(u.first_name),
                          last_name: safeStr(u.last_name),
                          role: safeStr(u.role) || "user",
                          is_approved: asBool(u.is_approved),
                          active: asBool(u.active),
                        };

                      const rowBusy = isBusy(id);

                      return (
                        <tr key={id} className={!asBool(u.active) ? "um-rowMuted" : ""}>
                          <td className="um-left">
                            <div className="um-username">{u.username}</div>
                            <div className="um-sub">{t("admin:userManagement.row.id", { id })}</div>
                          </td>

                          <td className="um-left">
                            <input
                              className="um-input"
                              value={d.first_name}
                              onChange={(e) => patchDraft(id, { first_name: e.target.value })}
                              onBlur={() => {
                                if (d.first_name !== safeStr(u.first_name)) {
                                  handleFieldUpdate(id, "first_name", d.first_name);
                                }
                              }}
                              disabled={rowBusy}
                            />
                          </td>

                          <td className="um-left">
                            <input
                              className="um-input"
                              value={d.last_name}
                              onChange={(e) => patchDraft(id, { last_name: e.target.value })}
                              onBlur={() => {
                                if (d.last_name !== safeStr(u.last_name)) {
                                  handleFieldUpdate(id, "last_name", d.last_name);
                                }
                              }}
                              disabled={rowBusy}
                            />
                          </td>

                          <td className="um-left">
                            <select
                              className="um-select"
                              value={d.role || "user"}
                              onChange={(e) => {
                                const nextRole = e.target.value;
                                patchDraft(id, { role: nextRole });

                                if (nextRole !== safeStr(u.role)) {
                                  handleFieldUpdate(id, "role", nextRole);
                                }
                              }}
                              disabled={rowBusy}
                            >
                              <option value="user">{t("admin:roles.user")}</option>
                              <option value="superuser">{t("admin:roles.superuser")}</option>
                              <option value="admin">{t("admin:roles.admin")}</option>
                              <option value="reception">{t("admin:roles.reception")}</option>
                            </select>
                            <div className="um-sub">{roleLabel(d.role, t)}</div>
                          </td>

                          <td className="um-center">
                            <input
                              type="checkbox"
                              className="um-checkbox"
                              checked={asBool(d.is_approved)}
                              onChange={(e) => {
                                const checked = e.target.checked;
                                patchDraft(id, { is_approved: checked });
                                handleFieldUpdate(id, "is_approved", checked);
                              }}
                              disabled={rowBusy}
                            />
                          </td>

                          <td className="um-center">
                            <input
                              type="checkbox"
                              className="um-checkbox"
                              checked={asBool(d.active)}
                              onChange={(e) => {
                                const checked = e.target.checked;
                                patchDraft(id, { active: checked });
                                handleFieldUpdate(id, "active", checked);
                              }}
                              disabled={rowBusy}
                            />
                          </td>

                          <td className="um-left">
                            <input
                              type="password"
                              className="um-input um-input--password"
                              placeholder={t("admin:userManagement.fields.newPassword")}
                              value={passwords[id] || ""}
                              onChange={(e) =>
                                setPasswords((prev) => ({ ...(prev || {}), [id]: e.target.value }))
                              }
                              disabled={rowBusy}
                            />
                          </td>

                          <td className="um-right">
                            <div className="um-actions">
                              <button
                                className="um-btn um-btn--small um-btn--warn"
                                onClick={() => handleResetPassword(id)}
                                disabled={rowBusy}
                                type="button"
                              >
                                {t("admin:userManagement.actions.resetPassword")}
                              </button>

                              <button
                                className="um-btn um-btn--small"
                                onClick={() => openSettings(id)}
                                disabled={rowBusy}
                                type="button"
                              >
                                {t("admin:userManagement.actions.settings")}
                              </button>

                              <button
                                className="um-btn um-btn--small um-btn--danger"
                                onClick={() => handleDeleteUser(id)}
                                disabled={rowBusy}
                                type="button"
                              >
                                {t("admin:userManagement.actions.delete")}
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="um-footnote">
                {t("admin:userManagement.footnote")}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

